@let currentUser = currentUser$ | async;
@let top3 = top3$ | async;

<div id="training-log-page">
  @if (isPrivate && (loading$ | async) === false) {
    <div class="private-training-log">
      <kin-icon [color]="black200" size="32" name="eye_off" />
      <p>{{ 'training_log.private' | translate }}</p>
      <kin-btn [color]="'light'" (onClick)="navBack()">{{
        'watch.header.back' | translate
      }}</kin-btn>
    </div>
  } @else if ((loading$ | async) === false && activity) {
    <div id="training-log-header">
      <button class="back-btn" (click)="navBack()">
        <span class="icon icon-arrow-left"></span>
      </button>
      <h2>{{ 'training_log.title' | translate }}</h2>
    </div>
    <div id="top-blocks">
      <!-- ACTIVITY CARD -->
      @if ((loading$ | async) === false) {
        <tl-activity-block
          id="activity"
          [trainingLogInfos]="trainingLogInfos"
          [activity]="activity" />
      } @else {
        <!-- ACTIVITY SKELETON -->
        <div id="activity" class="skeleton card activity-skeleton">
          <div id="header">
            <div id="top">
              <p-skeleton shape="circle" height="4rem" width="4rem" />
              <p-skeleton shape="circle" height="4rem" width="4rem" />
            </div>
            <p-skeleton width="100%" height="1.5rem" borderRadius="16px" />
          </div>
          <p-skeleton id="content" height="100%" />
          <div id="footer">
            <p-skeleton id="bar" width="16rem" height="1rem" />
          </div>
        </div>
      }
      <!-- DATA CARD 📊 -->
      <div id="data" class="card">
        <!-- SPEED -->
        @if ((loading$ | async) === false && activity) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.commun.speed' | translate
              }}</span>
              <span class="multi-data">
                <span class="data-label">{{
                  'training_log.data.label.average' | translate
                }}</span>
                {{
                  unit === 'metric'
                    ? activity.speed.avg.toFixed(1) + ' km/h'
                    : (activity.speed.avg / milesValue).toFixed(1) + ' mph'
                }}
              </span>
              <span class="multi-data">
                <span class="data-label">{{
                  'training_log.data.label.maximum' | translate
                }}</span>
                {{
                  unit === 'metric'
                    ? activity.speed.max.toFixed(1) + ' km/h'
                    : (activity.speed.max / milesValue).toFixed(1) + ' mph'
                }}
              </span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem" />
          </ng-container>
        }
        <!-- POWER -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.commun.power' | translate
              }}</span>
              <span class="multi-data">
                <span class="data-label">{{
                  'training_log.data.label.average' | translate
                }}</span>
                {{ activity.power.avg }} Watts
              </span>
              <span class="multi-data">
                <span class="data-label">{{
                  'training_log.data.label.maximum' | translate
                }}</span>
                {{ activity.power.max }} Watts
              </span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem"></p-skeleton>
          </ng-container>
        }
        <!-- CADENCE -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.commun.cadence' | translate
              }}</span>
              <span class="multi-data">
                <span class="data-label">{{
                  'training_log.data.label.average' | translate
                }}</span>
                {{ activity.cadence.avg }} rpm
              </span>
              <span class="multi-data">
                <span class="data-label">{{
                  'training_log.data.label.maximum' | translate
                }}</span>
                {{ activity.cadence.max }} rpm
              </span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem" />
          </ng-container>
        }
        <!-- DISTANCE -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.data.label.distance' | translate
              }}</span>
              <span class="data">
                {{
                  unit === 'metric'
                    ? activity.distance >= 1000
                      ? (activity.distance / 1000).toFixed(2) + ' km'
                      : activity.distance.toFixed(0) + ' m'
                    : activity.distance * feetValue >= 5280
                      ? ((activity.distance * feetValue) / 5280).toFixed(2) +
                        ' miles'
                      : (activity.distance * feetValue).toFixed(0) + ' feet'
                }}
              </span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem" />
          </ng-container>
        }
        <!-- SLOPE -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.commun.elevation' | translate
              }}</span>
              <span class="data">{{
                unit === 'metric'
                  ? activity.elevation_gain.toFixed(0) + ' m'
                  : (activity.elevation_gain * feetValue).toFixed(0) + ' feets'
              }}</span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem"></p-skeleton>
          </ng-container>
        }
        <!-- DURATION -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.data.label.duration' | translate
              }}</span>
              <span class="data">
                {{
                  activity.duration | duration: 'value' : 'format' : 'HH:mm:ss'
                }}
              </span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem"></p-skeleton>
          </ng-container>
        }
        <!-- CALORIES -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.data.label.calories' | translate
              }}</span>
              <span class="data"
                >{{ activity.accumulated_kcalories }} kcal</span
              >
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem"></p-skeleton>
          </ng-container>
        }
        <!-- EQUIPMENT -->
        @if ((loading$ | async) === false) {
          <ng-container>
            <div class="data-row">
              <span class="label">{{
                'training_log.data.label.equipment' | translate
              }}</span>
              <span class="data">
                {{ activity.equipment_manufacturer }} /
                {{ activity.equipment_model }}
              </span>
            </div>
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton width="100%" height="4rem"></p-skeleton>
          </ng-container>
        }
      </div>
    </div>
    <div class="divider"></div>
    <div id="content-blocks">
      <div class="first-column">
        <!-- ACTIONS BLOCK 🎳 -->
        @if ((loading$ | async) === false) {
          <app-tl-actions-block
            class="block"
            id="actions"
            [activity]="activity"
            [currentUser]="currentUser"
            (statusChange)="status.next($event)"
            (delete)="navBack()" />
        } @else {
          <ng-container>
            <div class="block actions-skeleton">
              <p-skeleton height="1rem" width="12rem"></p-skeleton>
              <div class="btn-row">
                <p-skeleton shape="circle" size="3rem"></p-skeleton>
                <p-skeleton shape="circle" size="3rem"></p-skeleton>
              </div>
              <div class="divider"></div>
              <div class="counter-row">
                <p-skeleton width="8rem" size="2.5rem"></p-skeleton>
                <p-skeleton width="8rem" size="2.5rem"></p-skeleton>
              </div>
            </div>
          </ng-container>
        }
        <!-- CHARTS BLOCK 📈 -->
        <div id="charts" class="block">
          <!-- HEADER BLOCK -->
          <div class="header-block">
            <h2>
              {{ 'training_log.bloc_header.activity_analysis' | translate }}
            </h2>
            <div class="switch-btn"></div>
          </div>
          <!-- SPEED CHARTS 🏎️ -->
          @if ((loading$ | async) === false) {
            <ng-container>
              <tl-chart-component
                chartType="line"
                [title]="'training_log.commun.speed' | translate"
                [metrics]="unit === 'metric' ? 'km/h' : 'mph'"
                [label]="speedDataChart.labels"
                [data]="speedDataChart.datasets[0].data"
                chartColor="#428FF3"></tl-chart-component>
            </ng-container>
          } @else {
            <ng-container [ngTemplateOutlet]="chartSkeleton"></ng-container>
          }
          <!-- POWER CHARTS ⚡ -->
          @if ((loading$ | async) === false) {
            <ng-container>
              <tl-chart-component
                chartType="line"
                [title]="'training_log.commun.power' | translate"
                metrics="watts"
                [label]="powerDataChart.labels"
                [data]="powerDataChart.datasets[0].data"
                chartColor="#FFEB3B"></tl-chart-component>
            </ng-container>
          } @else {
            <ng-container [ngTemplateOutlet]="chartSkeleton"></ng-container>
          }
          <!-- CADENCE CHARTS 🏃‍♂️ -->
          @if ((loading$ | async) === false) {
            <ng-container>
              <tl-chart-component
                chartType="line"
                [title]="'training_log.commun.cadence' | translate"
                metrics="rpm"
                [label]="cadenceDataChart.labels"
                [data]="cadenceDataChart.datasets[0].data"
                chartColor="#57A3B0"></tl-chart-component>
            </ng-container>
          } @else {
            <ng-container [ngTemplateOutlet]="chartSkeleton"></ng-container>
          }

          <!-- HEART RATE CHARTS ❤️ -->
          @if ((loading$ | async) === false && heartRateDataChart) {
            <tl-chart-component
              [chartType]="heartRateChartsPreferences.type"
              [title]="'training_log.commun.heart_rate' | translate"
              [metrics]="heartRateChartsPreferences.metric"
              [label]="heartRateDataChart.labels"
              [data]="heartRateDataChart.datasets[0].data"
              [chartColor]="heartRateChartsPreferences.color" />
          }

          <!-- TRAINING SECTIONS CHARTS 📊 -->
          @if ((loading$ | async) === false) {
            <ng-container>
              @if (trainingSections.length > 0) {
                <tl-ts-chart-component
                  title="Training Sections"
                  [unit]="unit"
                  [trainingsSections]="
                    trainingSections
                  "></tl-ts-chart-component>
              }
            </ng-container>
          } @else {
            <ng-container [ngTemplateOutlet]="chartSkeleton"></ng-container>
          }
          <!-- WORKOUT CHART 📊 -->
          @if ((loading$ | async) === false && activity.mode === 'workout') {
            <ng-container>
              @if ((loading$ | async) === false && workoutIntervals.value) {
                <ng-container>
                  <app-workout-chart
                    [workoutIntervals]="
                      workoutIntervals.value
                    "></app-workout-chart>
                </ng-container>
              } @else {
                <ng-container>
                  <div translate>training_log.charts.no_data</div>
                </ng-container>
              }
            </ng-container>
          }
          <div class="divider"></div>
          @if ((loading$ | async) === false) {
            <ng-container>
              <tl-ppr-chart
                [label]="pprDataChart.labels"
                [data]="pprDataChart.datasets[0].data"></tl-ppr-chart>
            </ng-container>
          } @else {
            <ng-container [ngTemplateOutlet]="chartSkeleton"></ng-container>
          }
        </div>
        <ng-template #chartSkeleton>
          <div class="chart-skeleton-header">
            <p-skeleton height="2rem" width="6rem"></p-skeleton>
            <p-skeleton height="2rem" width="6rem"></p-skeleton>
          </div>
          <p-skeleton width="100%" height="12rem"></p-skeleton>
        </ng-template>
      </div>
      <div class="second-column">
        @if (activity.mode !== 'outdoor') {
          @if ((loading$ | async) === false) {
            <ng-container>
              @if (
                (loading$ | async) === false &&
                activity.mode !== 'coaching' &&
                activity.mode !== 'workout' &&
                activity.mode !== 'free_ride' &&
                activity.mode !== 'gps_only'
              ) {
                <div class="bloc ranking">
                  <kin-header-block
                    size="medium"
                    viewMoreDisplay="true"
                    [label]="'watch.content.ranking.title' | translate"
                    [viewMoreLabel]="
                      'watch.content.ranking.view_all_btn' | translate
                    "
                    (viewMoreClick)="openModal('ranking', top3, currentUser)" />
                  <kin-ranking-block
                    [top3]="top3"
                    [user]="currentUser"
                    (podiumClick)="
                      openModal(
                        currentUser ? 'ranking' : 'connection',
                        top3,
                        currentUser
                      )
                    " />
                </div>
              }
              @if (
                (loading$ | async) === false && activity.mode === 'gps_only'
              ) {
                <kin-header-block
                  size="medium"
                  viewMoreDisplay="true"
                  [label]="'watch.content.ranking.title' | translate"
                  [viewMoreLabel]="
                    'watch.content.ranking.view_all_btn' | translate
                  "
                  (viewMoreClick)="openModal('ranking', top3, currentUser)" />
                <kin-ranking-block
                  [top3]="top3"
                  [user]="currentUser"
                  (podiumClick)="
                    openModal(
                      currentUser ? 'ranking' : 'connection',
                      top3,
                      currentUser
                    )
                  " />
              }
            </ng-container>
          } @else {
            <ng-container>
              <app-block-header [viewAll]="currentUser" [viewAll]="false">{{
                'watch.content.ranking.title' | translate
              }}</app-block-header>
              <p-skeleton height="26rem" width="100%"></p-skeleton>
            </ng-container>
          }
          <div class="divider"></div>
        }
        @if (
          (loading$ | async) === false &&
          activity.mode !== 'free_ride' &&
          activity.mode !== 'gps_only' &&
          activity.mode !== 'workout' &&
          activity.mode !== 'coaching' &&
          activity.mode !== 'outdoor'
        ) {
          <div id="video-card">
            <app-block-header [viewAll]="false">
              {{ 'training_log.bloc_header.video' | translate }}
            </app-block-header>
            @if ((loading$ | async) === false) {
              <ng-container>
                @if (video) {
                  <kin-scenic-card
                    (onLikeClick)="addVideoToFavorite(video.id)"
                    (onAuthorClick)="onAuthorCardClick(video.author.id)"
                    (onCardClick)="onVideoCardClick(video.id)"
                    [information]="video"
                    [details]="true" />
                }
              </ng-container>
            } @else {
              <ng-container>
                <kin-scenic-card [details]="true" />
              </ng-container>
            }
          </div>
        }
        @if ((loading$ | async) === false) {
          <ng-container>
            @if (activity.mode === 'gps_only' && (loading$ | async) === false) {
              <app-block-header [viewAll]="false">
                {{ 'training_log.bloc_header.gps' | translate }}
              </app-block-header>
              <kin-scenic-card [information]="gpsTrack" />
            }
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton height="14rem" width="100%" />
          </ng-container>
        }
        @if ((loading$ | async) === false) {
          <ng-container>
            @if (
              activity.mode === 'workout' &&
              (loading$ | async) === false &&
              workoutCardData
            ) {
              <kin-scenic-card
                [information]="workoutCardData"
                [details]="true" />
            }
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton height="14rem" width="100%" />
          </ng-container>
        }
        @if ((loading$ | async) === false) {
          <ng-container>
            @if (
              activity.mode === 'coaching' &&
              (loading$ | async) === false &&
              coachingCardData
            ) {
              <kin-scenic-card
                (onCardClick)="redirect(coachingCardData.id, 'browse')"
                (onAuthorClick)="redirect(coachingCardData.author.id, 'author')"
                [information]="coachingCardData"
                class="coaching-card" />
            }
          </ng-container>
        } @else {
          <ng-container>
            <p-skeleton height="14rem" width="100%" />
          </ng-container>
        }
      </div>
    </div>
  }
</div>
